import { Tooltip } from "antd"
import { FC, useEffect, useState } from "react"
import { RotationRightButton } from "../buttons/iconButtons/action/RotationCollection"
import BaseCommandsPanel from "./BaseCommandsPanel"
import FlexColumn from "../controls/FlexColumn"
import DefaultLotsByCommandsPanel from "./DefaultLotsByCommandsPanel"
import { DefaultDatePeriod, GridFilterAction } from "../../system/hooks/useGridFilter"
import { LotDataProvider } from "../../Services/DataProviders/LotDataProvider"
import { GoodsDataProvider } from "../../Services/DataProviders/GoodsDataProvider"
import { LotsByLotsCreatorModal } from "../../Business/Accounting/LotsByLots"
import { GoodsCreatorModal } from "../../Business/Dictionaries/Goods"
import { DatePeriodPanel } from "../../hoc/DatePeriodPanel/DatePeriodPanel"
import { IDatePeriod } from "../datePeriodPicker/datePeriodPicker"
import styles from "./styles/LotsCommandsPanel.module.scss"
import FlexRow from "../controls/FlexRow"
import { Accordion } from "../controls/accordion"
import { useTranslation } from "react-i18next"
import { DecimalInputV2, DecimalInputWithNegatives, NumberInput } from "../controls/inputs/BaseInput"
import { IdTableVariant } from "../../@types/enumsGlobal"
import { Button } from "../buttons/button"
import Checkbox from "../checkboxes/checkbox"
import { MessageModalWindow } from "../modalWindows/MessageModalWindow"
import { useTabsContext } from "../../system/providers/tabsProvider"
import { ICreateDocumentsRequest } from "../../libs/coreapi-dto/accounting/lot"

interface IPanelEvent {
    disabled?: boolean
    onClick: () => any    
    onSubItemClick?: () => any
}

interface IActualPricesPanelPanelProps { 
    refresh?: IPanelEvent
    selectedItem?: [IGridRow | undefined, (item) => void]
    dispatchGridFilter: (value: GridFilterAction) => void
    gridFilter: IGridFilter;
    lotDataProvider: LotDataProvider
    goodsDataProvider: GoodsDataProvider
    plugin: IPluginSettings
    setViewState: (viewState: GridStateType) => void
    selectAll: [boolean, (item) => void]
    isAllowCreateDocument: boolean
    selectedItems: IGridRow[]
    selectedItemsInverion: IGridRow[]
    allIds?: string[]
}

interface IMessageModalProps {
    show: boolean,
    message: string,
    handler?: () => void
}

interface IFilterDifferencePercent {
    percentMore: number
    percentLess: number
}

export const ActualPricesPanel: FC<IActualPricesPanelPanelProps> = (props) => {
    const tabCtx = useTabsContext();
    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(DefaultDatePeriod);
    const [dateBestBeforePeriod, setDateBestBeforePeriod] = useState<IDatePeriod>(DefaultDatePeriod);
    const [openedBestBefore, setOpenedBestBefore] = useState<boolean>(false)
    const [percentBestBefore, setPercentBestBefore] = useState<number>(0)
    const [openedDateSale, setOpenedDateSale] = useState<boolean>(false)
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: '' });
    const [filterDifferencePercent, setFilterDifferencePercent] = useState<IFilterDifferencePercent>({percentMore: 0, percentLess: 0})
    const [isGnvls, setIsGnvls] = useState<boolean>()
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    
    const { t } = useTranslation();
    const baseT = (value: string) => t('actualPrices.' + value) 
    
    useEffect(() => {
        const savedFilters = localStorage.getItem('journalFilters');
        if (savedFilters) {
            try {
                const filters = JSON.parse(savedFilters);
                if (filters.reportType === 'ActualPrice' && filters.filters?.DifferencePercent) {
                    const percentValue = Number(filters.filters.DifferencePercent);
                    const newFilter = {
                        percentMore: percentValue,
                        percentLess: 0
                    };
                    
                    setFilterDifferencePercent(newFilter);
                    setIsGnvls(filters.filters?.IsGnvls ?? false)
                    
                    applyDifferencePercentFilter(newFilter,filters.filters?.IsGnvls ?? false);
                }
            } catch (error) {
                console.error('Error parsing filters:', error);
            }
        }
        setInitialLoadComplete(true);

        return (() => localStorage.removeItem('journalFilters'))
    }, []);

    useEffect(() => {
        if (openedDateSale) {
            props.dispatchGridFilter({
                ...props.gridFilter,
                type: "paramFilter",
                payload: {
                    gridParamFilter: {
                        ...props.gridFilter.paramFilter,
                        dateSaleFrom: datePeriod.startDate,
                        dateSaleTo: datePeriod.endDate
                    },
                },
            });
        }       
    },[datePeriod])

    useEffect(() => {
        props.dispatchGridFilter({
            type: 'paramSimpleFilter',
            payload: {
                gridParamFilter: props.gridFilter.paramFilter,
                gridColumnFilter: [...props.gridFilter.columnFilters.filter(c => c.name != 'seriesBestBefore'),
                    ...(dateBestBeforePeriod?.startDate
                        ? [
                            {
                                name: "seriesBestBefore",
                                value: dateBestBeforePeriod.startDate.toFormat("yyyyMMdd"),
                                operator: "MoreOrEq",
                            } as IGridColumnFilter,
                        ]
                        : []),
                    ...(dateBestBeforePeriod?.endDate
                        ? [
                            {
                                name: "seriesBestBefore",
                                value: dateBestBeforePeriod.endDate.toFormat("yyyyMMdd"),
                                operator: "LessOrEq",
                            } as IGridColumnFilter,
                        ]
                        : []),
                ],
            }
        })
    },[dateBestBeforePeriod])

    useEffect(() => {
        props.dispatchGridFilter({
            type: 'paramSimpleFilter',
            payload: {
                gridParamFilter: props.gridFilter.paramFilter,
                gridColumnFilter: [...props.gridFilter.columnFilters.filter(c => c.name !== "percentBestBefore"), 
                    {
                        name: "percentBestBefore",
                        operator: "MoreOrEq",
                        value: percentBestBefore
                    }]
            }
        })
    },[percentBestBefore])

    const applyDifferencePercentFilter = (percent: IFilterDifferencePercent, isGnvls?: boolean) => {
        const { percentMore, percentLess } = percent;
        
        let expressionParts: string[] = [];
        
        if (percentMore !== 0) {
            expressionParts.push(`"DifferencePercent" > ${percentMore}`);
        }
        
        if (percentLess !== 0) {
            expressionParts.push(`"DifferencePercent" < ${percentLess}`);
        }

        if (isGnvls != undefined) {
            expressionParts.push(`"IsGnvls" = ${isGnvls}`)
        }
    
        const expression = expressionParts.join(" and ");
        
        if (expression) {
            props.dispatchGridFilter({
                type: "addCustomWhereExpressionFilter",
                payload: expression
            });
        } else {
            props.dispatchGridFilter({ 
                type: "deleteCustomWhereExpressionFilter" 
            });
        }
    };

    const handlePercentChange = (type: 'more' | 'less', value: number) => {
        const newFilter = {
            ...filterDifferencePercent,
            [`percent${type === 'more' ? 'More' : 'Less'}`]: value
        };
        
        setFilterDifferencePercent(newFilter);
        applyDifferencePercentFilter(newFilter,isGnvls);
    };
    
    const createActRevaluation = (idTableVariant: IdTableVariant) => {
         let lotsId = [] as string[]

        if (!props.selectAll[0]) {
            lotsId = props.selectedItems.map(c => { return c.idGlobal })
        } else {
            if (props.selectedItemsInverion.length > 0) 
            {
                lotsId = props.selectedItemsInverion.map(c => { return c.idGlobal })
            }
            else {
                lotsId = props.allIds ?? props.selectedItems.map(c => { return c.idGlobal })
            }
        }
        
        const request = {
            idTable: idTableVariant,
            idLotsGlobal: lotsId
        } as ICreateDocumentsRequest 
        
        props.lotDataProvider.createActRevaluation(request, (response) => {
            props.setViewState('refresh')
            setShowMessageModal({message: `Документ ${response.mnemocode} создан, желаете перейти для дальнейшей работы?`,
            show: true,
            handler: () => {
                switch(idTableVariant){
                    case IdTableVariant.ActRevaluation2 : {
                        return tabCtx.openChild('act_revaluation_creator_plugin',"edit",response.idGlobal)
                    }
                }
            }})
        })
    }
    
    return <>
        <FlexColumn>
            <FlexRow>
                <BaseCommandsPanel groups={[
                    [
                        {
                            iconButton: <Tooltip title={'Refresh'}>
                                <RotationRightButton sizeVariant="mini"  {...{...props.refresh}} />
                            </Tooltip>
                        }
                    ]
                
                ]} />
            </FlexRow>

            <Accordion opened={true} caption={baseT('filterDifferencePercent')} id={"filterDifferencePercent"}>
                <FlexRow>
                    <DecimalInputWithNegatives
                        inline
                        label={baseT('more')} 
                        value={filterDifferencePercent.percentMore}
                        onChange={(value) => handlePercentChange('more', value)}
                    />
                    <DecimalInputWithNegatives
                        inline
                        label={baseT('less')}
                        value={filterDifferencePercent.percentLess}
                        onChange={(value) => handlePercentChange('less', value)}
                    />
                </FlexRow>
            </Accordion>

            <Accordion opened={openedDateSale} onOpen={setOpenedDateSale} caption={baseT('dateSale')} id={"dateSale"}>
                <FlexColumn className={styles.flexColumn}>
                    <DatePeriodPanel
                        datePeriod={datePeriod}
                        onDatePeriodChange={(date: IDatePeriod) => {
                            setDatePeriod(date);
                        }}
                    /> 
               </FlexColumn>
            </Accordion>

            <Accordion opened={openedBestBefore} onOpen={setOpenedBestBefore} caption={baseT('seriesBestBefore')} id={"SeriesBestBefore"}>
                <FlexColumn className={styles.flexColumn}>
                    <DatePeriodPanel
                        datePeriod={dateBestBeforePeriod}
                        onDatePeriodChange={(date: IDatePeriod) => {
                            setDateBestBeforePeriod(date)
                        }}
                    />
                     <NumberInput className={styles.numberInput}label={baseT('percentBestBefore')} value={percentBestBefore} 
                        onChange={setPercentBestBefore}
                        max={100}
                        min={0}
                    />
                </FlexColumn>
            </Accordion>
            
            
            <DefaultLotsByCommandsPanel
                rootTranslation='accounting.lotsByLots.'
                selectedItem={props.selectedItem}
                dispatchGridFilter={props.dispatchGridFilter}
                dataProvider={props.lotDataProvider}
                goodsDataProvider={props.goodsDataProvider}
                creatorModalJsx={LotsByLotsCreatorModal}
                createGoodsModalJsx={GoodsCreatorModal}
                pluginSettings={props.plugin}
                setViewState={(vs) => props.setViewState(vs)}
                isHidePanel
            />
            <FlexRow className={styles.flexRow}>
                <Button className={styles.button} variant={"primary"} disabled={props.isAllowCreateDocument}
                    onClick={() => createActRevaluation(IdTableVariant.ActRevaluation2)}>
                    {baseT('createActRevaluation')}
                </Button>
            </FlexRow>
            <div><Checkbox checked={props.selectAll[0]} label={baseT('selectAll')} onChange={props.selectAll[1]} name={""} /></div>
        </FlexColumn>

         {showMessageModal.show &&
            <MessageModalWindow
                ok={{ onClick: () => { 
                    if (showMessageModal.handler) { 
                        showMessageModal.handler() 
                    }
                    setShowMessageModal({ show: false, message: '' }) 
                }}}
                cancel={{ onClick: () => {
                    setShowMessageModal({ show: false, message: '' })
                    } }}
                message={showMessageModal.message}
            />
        }
       
    </>

}